import { apiClient } from './client';

export interface DevFeature {
  id: number;
  parent_id: number | null;
  name: string;
  description: string | null;
  status: 'complete' | 'partial' | 'planned' | 'broken' | 'deprecated';
  component_path: string | null;
  route_path: string | null;
  notes: string | null;
  sort_order: number;
  quality_rating: number | null;
  completion_pct: number | null;
  created_at: string;
  updated_at: string;
}

export interface DevTask {
  id: number;
  title: string;
  description: string | null;
  status: 'open' | 'in_progress' | 'completed' | 'wont_fix';
  priority: 'critical' | 'high' | 'medium' | 'low';
  category: 'bug' | 'feature' | 'refactor' | 'ux' | 'performance' | 'security' | 'content' | 'infra';
  feature_id: number | null;
  feature_name?: string;
  assigned_to: string | null;
  completed_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface DevRoadmapItem {
  id: number;
  title: string;
  description: string | null;
  status: 'planned' | 'in_progress' | 'completed' | 'cancelled';
  milestone: string | null;
  target_date: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface DevSummary {
  features: { total: number; complete: number; partial: number; planned: number; broken: number };
  tasks: { total: number; open_count: number; in_progress: number; completed: number; critical: number };
  roadmap: { total: number; completed: number; in_progress: number; planned: number };
  agentLog: string;
}

export async function getDevSummary(): Promise<DevSummary> {
  const r = await apiClient.get('/dev/summary');
  return r.data.data;
}

export async function getDevFeatures(): Promise<DevFeature[]> {
  const r = await apiClient.get('/dev/features');
  return r.data.data;
}

export async function createDevFeature(data: Partial<DevFeature>): Promise<{ id: number }> {
  const r = await apiClient.post('/dev/features', data);
  return r.data;
}

export async function updateDevFeature(id: number, data: Partial<DevFeature>): Promise<void> {
  await apiClient.put(`/dev/features/${id}`, data);
}

export async function deleteDevFeature(id: number): Promise<void> {
  await apiClient.delete(`/dev/features/${id}`);
}

export async function getDevTasks(filters?: { status?: string; priority?: string; category?: string }): Promise<DevTask[]> {
  const r = await apiClient.get('/dev/tasks', { params: filters });
  return r.data.data;
}

export async function createDevTask(data: Partial<DevTask>): Promise<{ id: number }> {
  const r = await apiClient.post('/dev/tasks', data);
  return r.data;
}

export async function updateDevTask(id: number, data: Partial<DevTask>): Promise<void> {
  await apiClient.put(`/dev/tasks/${id}`, data);
}

export async function deleteDevTask(id: number): Promise<void> {
  await apiClient.delete(`/dev/tasks/${id}`);
}

export async function getDevRoadmap(): Promise<DevRoadmapItem[]> {
  const r = await apiClient.get('/dev/roadmap');
  return r.data.data;
}

export async function createDevRoadmapItem(data: Partial<DevRoadmapItem>): Promise<{ id: number }> {
  const r = await apiClient.post('/dev/roadmap', data);
  return r.data;
}

export async function updateDevRoadmapItem(id: number, data: Partial<DevRoadmapItem>): Promise<void> {
  await apiClient.put(`/dev/roadmap/${id}`, data);
}

export async function deleteDevRoadmapItem(id: number): Promise<void> {
  await apiClient.delete(`/dev/roadmap/${id}`);
}

export async function backupCodeToDrive(): Promise<{ name: string; id: string; size: number }> {
  const r = await apiClient.post('/dev/backup-code');
  return r.data.data;
}

export async function ensurePreviewAccounts(): Promise<{
  employee: { email: string; password: string };
  client: { email: string; password: string };
}> {
  const r = await apiClient.post('/dev/ensure-preview-accounts');
  return r.data.data;
}

export async function getPortalPreviewTokens(): Promise<{
  employee: { token: string; refreshToken: string; username: string; email: string };
  client: { token: string; refreshToken: string; username: string; email: string };
}> {
  const r = await apiClient.get('/dev/portal-preview-tokens');
  return r.data.data;
}

// Mint portal tokens to view a specific client's/employee's real portal (admin-only).
export async function getPortalTokenFor(
  type: 'client' | 'employee',
  recordId: number,
): Promise<{ token: string; refreshToken: string; username: string; role: string }> {
  const r = await apiClient.get(`/dev/portal-token/${type}/${recordId}`);
  return r.data.data;
}
